import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import './Chart.css';
import { getTouchpointColorScale } from '../utils/touchpointColors';

const FrequencyChart = ({ countyData, indianaAverages, years, touchpointTypes }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [selectedTouchpoint, setSelectedTouchpoint] = useState('All');
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const tooltipDefinitions = {
    'ED visit': 'Instances where the decedent had a chief complaint or diagnosis in an emergency department.',
    'EMS interaction': 'Patient care data on EMS events collected by the Indiana Department of Homeland Security.',
    'Jail booking': 'When decedents were booked into a county jail facility.',
    'Release from prison': 'The decedent\'s last release date from a prison facility located in Indiana.',
    'Rx dispensation': 'Dispensation of controlled substances which could include opioid pain analgesics and evidence-based medications for opioid-use disorder.',
    'Rx dispensation for Buprenorphine': 'Dispensation of Buprenorphine, an evidence-based medication for opioid-use disorder.'
  };

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const dataSource = countyData || indianaAverages;
    if (!dataSource || dimensions.width === 0) {
        return;
    }

    const numericYears = years.filter(year => /^\d{4}$/.test(year));
    const yearsToDisplay = [...numericYears, 'Last Two Years'];

    const dataForChart = [];
    const typesToDisplay = selectedTouchpoint === 'All' ? touchpointTypes : [selectedTouchpoint];
    
    typesToDisplay.forEach(type => {
      const values = yearsToDisplay.map(year => ({
        year: year,
        value: dataSource.touchpoints[type]?.[year]?.['Average Touchpoint Utilization Count'] || 0
      }));
      dataForChart.push({ type, values });
    });

    if (selectedTouchpoint !== 'All' && countyData && indianaAverages) {
      const stateValues = yearsToDisplay.map(year => ({
        year: year,
        value: indianaAverages.touchpoints[selectedTouchpoint]?.[year]?.['Average Touchpoint Utilization Count'] || 0
      }));
      dataForChart.push({ type: 'State Average', values: stateValues });
    }

    const margin = { top: 20, right: 30, bottom: 100, left: 40 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    svg
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom);
    
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // 3. Scales
    // Use scalePoint to handle both numeric years and "Last Two Years"
    const x = d3.scalePoint()
      .domain(yearsToDisplay)
      .range([0, width])
      .padding(0.1);

    const allValues = [];
    dataForChart.forEach(d => {
      d.values.forEach(v => allValues.push(v.value));
    });
    const yMax = d3.max(allValues);
    const y = d3.scaleLinear()
      .domain([0, yMax > 0 ? yMax : 1])
      .nice()
      .range([height, 0]);

    const color = getTouchpointColorScale(touchpointTypes);

    // 4. Axes
    g.append('g').attr('transform', `translate(0,${height})`).call(d3.axisBottom(x));
    g.append('g').call(d3.axisLeft(y));

    // 5. Line Generator
    const line = d3.line()
      .x(d => x(d.year))
      .y(d => y(d.value))
      .defined(d => d.value != null);

    // 6. Draw Lines
    const series = g.selectAll('.series')
      .data(dataForChart)
      .enter()
      .append('g')
      .attr('class', 'series');

    series.append('path')
      .attr('class', 'line')
      .attr('d', d => line(d.values))
      .style('stroke', d => d.type === 'State Average' ? '#888' : color(d.type))
      .style('stroke-width', 2)
      .style('stroke-dasharray', d => d.type === 'State Average' ? '5,5' : 'none')
      .style('fill', 'none');

// 7. Legend (Horizontal, Wrapping Layout)
    const legendContainer = g.append('g')
      .attr('class', 'legend-container')
      .attr('transform', `translate(0, ${height + 40})`);

    const legendData = selectedTouchpoint === 'All' 
      ? touchpointTypes 
      : (countyData && indianaAverages ? [selectedTouchpoint, 'State Average'] : [selectedTouchpoint]);

    const legendItems = legendContainer.selectAll('.legend-item')
      .data(legendData)
      .enter()
      .append('g')
      .attr('class', 'legend-item')
      .style('cursor', 'default');

    legendItems.each(function(d) {
      if (d === 'State Average') {
        d3.select(this).append('line')
          .attr('x1', 0)
          .attr('y1', 9)
          .attr('x2', 18)
          .attr('y2', 9)
          .style('stroke', '#888')
          .style('stroke-width', 2)
          .style('stroke-dasharray', '5,5');
      } else {
        d3.select(this).append('rect')
          .attr('width', 18)
          .attr('height', 18)
          .style('fill', color(d));
      }
    });

    legendItems.append('text')
      .attr('x', 24)
      .attr('y', 9)
      .attr('dy', '.35em')
      .style('text-anchor', 'start')
      .style('font-size', '12px')
      .text(d => d);

    // Position legend items, wrapping to a new line if necessary
    let legendOffsetX = 0;
    let legendOffsetY = 0;
    const legendPadding = 15;
    const rowHeight = 25;
    const legendItemPositions = []; // Store final positions for tooltips

    legendItems.each(function() {
      const itemBBox = this.getBBox();
      const itemWidth = itemBBox.width;

      // If adding this item would exceed the chart width, wrap to the next line
      if (legendOffsetX > 0 && legendOffsetX + itemWidth > width) {
        legendOffsetX = 0;
        legendOffsetY += rowHeight;
      }

      d3.select(this).attr('transform', `translate(${legendOffsetX}, ${legendOffsetY})`);
      
      // Store the absolute position relative to the main 'g' element for tooltip positioning
      legendItemPositions.push({
        x: legendOffsetX,
        y: legendOffsetY + height + 40, // Add container's Y offset
        width: itemWidth
      });

      legendOffsetX += itemWidth + legendPadding;
    });


    // 8. Tooltips for Legend
    const tooltipLayer = g.append('g')
      .attr('class', 'tooltip-layer')
      .style('pointer-events', 'none');

    const tooltips = tooltipLayer.selectAll('.legend-tooltip')
      .data(touchpointTypes)
      .enter()
      .append('g')
      .attr('class', 'legend-tooltip')
      .style('opacity', 0);

    tooltips.append('rect')
      .attr('x', 0)
      .attr('y', -10)
      .attr('width', 400)
      .attr('height', 0)
      .attr('rx', 4)
      .style('fill', 'white')
      .style('stroke', '#ddd')
      .style('stroke-width', 1)
      .style('filter', 'drop-shadow(0 2px 8px rgba(0,0,0,0.15))');

    tooltips.append('foreignObject')
      .attr('x', 12)
      .attr('y', 2)
      .attr('width', 376)
      .attr('height', 0)
      .style('font-size', '0.85rem')
      .style('color', '#333')
      .style('line-height', '1.4')
      .append('xhtml:div')
      .style('padding', '0')
      .text(d => tooltipDefinitions[d] || '');

    legendItems.on('mouseover', function(event, d) {
      if (d === 'State Average') return;
      const legendIndex = legendData.indexOf(d);
      if (legendIndex === -1) return;
      const touchpointIndex = touchpointTypes.indexOf(d);
      if (touchpointIndex === -1) return;
      const tooltipGroup = tooltips.filter((td, i) => i === touchpointIndex);
      const foreignObject = tooltipGroup.select('foreignObject');
      const div = foreignObject.select('div');
      
      div.text(tooltipDefinitions[d] || '');
      const divNode = div.node();
      const textHeight = divNode ? divNode.scrollHeight + 20 : 40;
      
      foreignObject.attr('height', textHeight);
      tooltipGroup.select('rect').attr('height', textHeight);

      const itemPos = legendItemPositions[legendIndex];
      const tooltipWidth = 400;

      let tooltipX = itemPos.x + (itemPos.width / 2) - (tooltipWidth / 2);
      const tooltipY = itemPos.y - textHeight + 10; 

      tooltipX = Math.max(0, Math.min(tooltipX, width - tooltipWidth));

      tooltipGroup.attr('transform', `translate(${tooltipX}, ${tooltipY})`);
      tooltipGroup.transition().duration(200).style('opacity', 1);
    });

    legendItems.on('mouseout', function(event, d) {
      if (d === 'State Average') return;
      const touchpointIndex = touchpointTypes.indexOf(d);
      if (touchpointIndex === -1) return;
      const tooltipGroup = tooltips.filter((td, i) => i === touchpointIndex);
      tooltipGroup.transition().duration(200).style('opacity', 0);
    });
  }, [countyData, indianaAverages, years, touchpointTypes, selectedTouchpoint, dimensions.width, dimensions.height]);

  return (
    <div ref={containerRef} className="chart-container frequency-chart-container">
      {!countyData && !indianaAverages && <div className="placeholder">Select a county to see frequency trends.</div>}
      <select
        className="touchpoint-selector"
        value={selectedTouchpoint}
        onChange={(e) => setSelectedTouchpoint(e.target.value)}
      >
        <option value="All">All</option>
        {touchpointTypes.map(type => (
          <option key={type} value={type}>{type}</option>
        ))}
      </select>
      <svg ref={svgRef} />
    </div>
  );
};

export default FrequencyChart;